import { useCallback, useEffect, useState } from 'react';
import {
  trackAnalyticsEvent,
  PRODUCT_AREA,
  EVENT_NAME,
} from '@leagueplatform/analytics';
import {
  getAllAppointmentIframeAllowedOrigins,
  isMockAppointmentIframeEnabled,
} from 'common/env';
import {
  ANALYTICS_SCREEN_NAMES,
  BookingSource,
  SUB_PRODUCT_AREA_NAMES,
} from 'common/constants';
import { usePatientPortalContext } from 'contexts/patient-portal-provider.context';
import { getBookingSourceFromOrigin } from 'utils/origin-system-id';

export const BOOKING_MESSAGE_TYPES = {
  /** Fired when the user successfully completes a booking. */
  BOOKING_COMPLETED: 'BOOKING_COMPLETED',
  /** Fired when the user completes a booking and should be returned home. */
  BOOKING_COMPLETED_GO_HOME: 'BOOKING_COMPLETED_GO_HOME',
} as const;

type BookingMessageType =
  (typeof BOOKING_MESSAGE_TYPES)[keyof typeof BOOKING_MESSAGE_TYPES];

export type BookingFlowStatus = 'pending' | 'completed' | 'completed-go-home';

/**
 * Reports a completed booking, attributed to the system that owns the iframe
 * origin the completion message came from. This is deliberately the message
 * origin rather than the system the app is running under — except for the mock
 * iframe, which the portal serves itself and so has no owning system.
 */
function trackBookingCompleted(bookingSource: BookingSource) {
  trackAnalyticsEvent(EVENT_NAME.SCREEN_LOADED, {
    product_area: PRODUCT_AREA.GET_CARE,
    sub_product_area: SUB_PRODUCT_AREA_NAMES.APPOINTMENT,
    screen_name: ANALYTICS_SCREEN_NAMES.APPOINTMENT_BOOKING_IFRAME,
    resource: {
      id: '',
      type: 'virtual',
      status: 'completed',
      linked_name: bookingSource,
      linked_object_type: 'appointment booking partner',
    },
  });
}

function isBookingMessage(data: unknown): data is { type: BookingMessageType } {
  if (typeof data !== 'object' || data === null) return false;
  const { type } = data as { type?: unknown };
  return (
    type === BOOKING_MESSAGE_TYPES.BOOKING_COMPLETED ||
    type === BOOKING_MESSAGE_TYPES.BOOKING_COMPLETED_GO_HOME
  );
}

export function useIframeFlowStatus(): { status: BookingFlowStatus } {
  const [status, setStatus] = useState<BookingFlowStatus>('pending');
  const { systemId } = usePatientPortalContext();

  useEffect(() => {
    if (!isMockAppointmentIframeEnabled()) {
      const origins = getAllAppointmentIframeAllowedOrigins();
      if (origins.length === 0) {
        // eslint-disable-next-line no-console
        console.warn(
          '[patient-portal] useIframeFlowStatus: neither ' +
            'VITE_AMPLAR_APPOINTMENT_IFRAME_ALLOWED_ORIGINS nor ' +
            'VITE_MY_HEALTH_APPOINTMENT_IFRAME_ALLOWED_ORIGINS is configured. ' +
            'All inbound postMessage events will be blocked. Set these to the ' +
            'iframe origin(s) to enable the booking flow.',
        );
      }
    }
  }, []);

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      const originBookingSource = getBookingSourceFromOrigin(event.origin);

      const isMockOrigin =
        isMockAppointmentIframeEnabled() &&
        event.origin === window.location.origin;

      if (originBookingSource === null && !isMockOrigin) {
        return;
      }

      if (!isBookingMessage(event.data)) {
        return;
      }

      // The mock iframe is served by the portal itself, so its origin belongs
      // to no booking partner — attribute it to the system the app is running
      // as, so mock runs still report a booking source.
      const bookingSource = originBookingSource ?? systemId;

      if (event.data.type === BOOKING_MESSAGE_TYPES.BOOKING_COMPLETED) {
        trackBookingCompleted(bookingSource);
        setStatus('completed');
        return;
      }

      if (event.data.type === BOOKING_MESSAGE_TYPES.BOOKING_COMPLETED_GO_HOME) {
        setStatus('completed-go-home');
      }
    },
    [systemId],
  );

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  return { status };
}
